import { Suspense } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import clsx from 'clsx';
import slugify from 'slugify';
import {
  MapPinIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
} from '@heroicons/react/24/outline';

import type { OmniumStore } from '@fagmobler/omnium';
import type { FagmoblerInventory, FagmoblerOmniumCart } from '@fagmobler/types';
import { Button, Sticker, StockIndicators } from '@fagmobler/ui';

import { getCombinedInventoryFromProducts } from '@/lib/omnium/utils';
import { regions as noRegions } from '@/lib/no-regions';

import Layout from '@/components/layout';
import { useCart } from '@/components/cart';
import {
  StoreLocatorHeader,
  StoreAddress,
  useStores,
  useStoresWithStock,
  useStoresByRegion,
  useStoresInRegion,
} from '@/components/store';
import type { Region } from '@/components/store';
import { getCartStockStatusForStore } from '@/lib/omnium/utils';
import StoreHasCartInStockIndicator from '../store/StoreHasCartInStockIndicator';

const StoreMap = dynamic(() => import('@/components/store/StoreMap'), {
  ssr: false,
});

const regionsBySlug = Object.keys(noRegions).reduce(
  (acc: Record<string, Region>, key) => {
    const region = noRegions[key as keyof typeof noRegions];

    acc[slugify(region.name, { lower: true })] = region;

    return acc;
  },
  {}
);

export type StoreListingProps = {
  cart?: FagmoblerOmniumCart;
  inventory?: FagmoblerInventory;
  stores?: OmniumStore[];
};

export function StoreListing({
  cart: initialCart,
  stores: initialStores,
}: StoreListingProps) {
  const {
    cart: { data: cart = {} },
  } = useCart({ initialData: { cart: initialCart } });
  const {
    current: { data: { store: rawSelectedStore = null } = {} },
    stores: { data: rawStores = [] },
  } = useStores({
    initialData: {
      stores: initialStores,
    },
  });

  const { query, push } = useRouter();
  const region = regionsBySlug[query?.region as string] ?? null;

  const cartInventory = getCombinedInventoryFromProducts(cart?.products ?? []);

  const stores = useStoresWithStock(rawStores ?? [], cartInventory);
  const [selectedStore] = useStoresWithStock(
    rawSelectedStore ? [rawSelectedStore] : [],
    cartInventory
  );

  const storesByRegion = useStoresByRegion(stores);
  const storesInRegion = useStoresInRegion(storesByRegion, region);

  return (
    <Layout fullwidth>
      <div className={clsx('flex')}>
        <div
          className={clsx('w-full md:max-w-[28rem] lg:max-w-[32rem]', 'p-6')}
        >
          {!region ? (
            <>
              <StoreLocatorHeader>Velg fylke</StoreLocatorHeader>
              <ul>
                {Object.values(storesByRegion)
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((region) => (
                    <li key={region.iso}>
                      <Link
                        legacyBehavior
                        href={`/min-butikk/${slugify(region.name, {
                          lower: true,
                        })}`}
                        passHref
                        shallow
                      >
                        <button
                          className={clsx(
                            'relative',
                            'w-full',
                            'mt-2',
                            'p-4',
                            'bg-white',
                            'shadow-sm',
                            'rounded-md',
                            'flex',
                            'justify-between',
                            'text-left'
                          )}
                        >
                          <MapPinIcon
                            className={clsx('h-6', 'w-6', 'ml-2', 'mr-4')}
                          />
                          <span className={clsx('flex-grow')}>
                            {region.name}
                          </span>{' '}
                          <ChevronRightIcon className={clsx('h-6', 'w-6')} />
                        </button>
                      </Link>
                    </li>
                  ))}
              </ul>
            </>
          ) : (
            <>
              <Link legacyBehavior href="/min-butikk" passHref shallow>
                <button className={clsx('m-2', 'flex', 'items-center')}>
                  <ArrowLeftIcon className={clsx('w-4', 'h-4', 'mr-2')} />
                  Vis alle fylker
                </button>
              </Link>
              <StoreLocatorHeader>
                Velg butikk i {region?.name}
              </StoreLocatorHeader>
              <ul>
                {storesInRegion.map((store) => {
                  const isSelected = store.id === selectedStore?.id;
                  return (
                    <li
                      key={store.id}
                      className={clsx(
                        'w-full',
                        'mt-2',
                        'p-4',
                        'bg-white',
                        'shadow-lg',
                        'rounded-md',
                        'flex',
                        'text-left'
                      )}
                    >
                      <div className="w-full">
                        {isSelected && (
                          <div className={clsx('mb-2')}>
                            <Sticker size="sm" className="uppercase">
                              Min butikk
                            </Sticker>
                          </div>
                        )}
                        <StoreAddress store={store} />
                        <StoreHasCartInStockIndicator
                          storeHasCartInStock={getCartStockStatusForStore(
                            cart && cart.products && cart.products.length > 0
                              ? cart
                              : {},
                            store
                          )}
                        />
                      </div>
                      <div className="flex items-center pl-2">
                        <Link
                          legacyBehavior
                          href={`/min-butikk/${slugify(region?.name ?? '', {
                            lower: true,
                          })}/${slugify(store.name ?? '', { lower: true })}`}
                          passHref
                        >
                          <Button className="whitespace-nowrap">
                            Les om butikk
                          </Button>
                        </Link>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
        <div
          className={clsx(
            'hidden',
            'md:block',
            'w-full',
            'h-[calc(theme(height.screen)-theme(height.16))]'
          )}
        >
          <Suspense fallback={'Loading...'}>
            <StoreMap
              stores={region ? storesInRegion : stores}
              setFocusStore={(store) => {
                if (!store.name) return;
                const storeRegion = store?.externalIdsList?.find(
                  ({ providerName }) => providerName === 'Fylke'
                );

                const region =
                  noRegions[storeRegion?.id as keyof typeof noRegions];

                if (!region) return;

                const regionSlug = slugify(region.name, { lower: true });
                const storeSlug = slugify(store.name, { lower: true });

                if (regionSlug && storeSlug) {
                  push(`/min-butikk/${regionSlug}/${storeSlug}`);
                }
              }}
              center={{ lat: 65.865487, lng: 11.971709 }} //center of norway
              zoom={5}
            />
          </Suspense>
        </div>
      </div>
    </Layout>
  );
}

export default StoreListing;
